<template>
  <div class="repeatable-wrapper panel bg-gray-1 padding-l">
    <div
      v-for="(item, index) of safeValue"
      :key="uniqueId + '-row-' + index"
      class="panel padding-l margin-l-bottom animate--fast"
      :cleanItem="getItemClean(item)">
      <div class="item-wrapper flex-middle gap-m flex-wrap">
        <h5 class="margin-remove min-width-200">
          {{
            translateTitleCase("core.entity.translations.localeTitle", {
              locale: getLocaleLabel(index),
            })
          }}
        </h5>
        <div class="actions flex flex-wrap gap-m">
          <form-button
            icon="pencil"
            text="core.form.translations.editCta"
            @click.stop.prevent="showLanguageForEdit(index)"></form-button>
          <form-button
            icon="trash"
            theme="danger"
            text="core.form.translations.delete"
            @click.stop.prevent="startDeleteTranslationFlow(index)"></form-button>
        </div>
      </div>
    </div>

    <modal size="full" :show="showModal" @modal:closed="showModal = false">
      <template #title="{ modal }">
        <h3 class="modal__title" :class="{ 'modal__title--center': modal.centerTitle }">
          {{
            tu("core.form.translations.editModalTitle", {
              locale: getLocaleLabel(currentEditLanguage),
            })
          }}
        </h3>
      </template>
      <template #default>
        <div
          :item="value[currentEditLanguage]"
          :index="value[currentEditLanguage]"
          :cleanItem="getItemClean(value[currentEditLanguage])">
          <div class="item form">
            <div class="item-fields-wrapper width-expand">
              <component
                :is="group.component"
                v-bind="group.props"
                v-for="(group, groupIndex) of getGroupedFields()">
                <form-input
                  v-for="(field, fieldIndex) of group.fields"
                  v-bind="field"
                  :id="name + currentEditLanguage + fieldIndex"
                  :key="uniqueId + '-cell-' + fieldIndex"
                  class="field-immediate-wrapper animate--fast width-expand"
                  :model-value="value[currentEditLanguage][fieldIndex]"
                  :name="name + '-' + currentEditLanguage + '-' + fieldIndex"
                  :validation="fields[fieldIndex]?.validation"
                  :do-validations="true"
                  :type="fields[fieldIndex]?.type"
                  @field:changed="
                    (val) => (value[currentEditLanguage][fieldIndex] = val)
                  " />
              </component>
            </div>
          </div>
        </div>
      </template>
    </modal>

    <div class="repeatable-field-footer">
      <div v-if="hasUntranslatedLocales" class="flex flex-end gap-m flex-center">
        <form-input
          v-model="newLocaleAddValue"
          :error-message="
            hasAddError ? t('core.entity.translations.selectLocaleForAddError') : ''
          "
          type="select2"
          :list="untranslatedLocalesList"
          :translate-list="false"></form-input>
        <form-button icon="plus" type="primary" @click.prevent="addRow">
          <span v-if="safeValue && valueLength > 0">{{
            translate("core.form.translations.addAnother")
          }}</span>
          <span v-else>{{ translate("core.form.translations.addFirst") }}</span>
        </form-button>
      </div>
      <div v-if="!hasUntranslatedLocales" class="flex flex-end">
        <p>
          {{ translate("core.entity.translations.allTranslationsCompleteExplain") }}
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import TranslationsInputMixin from "@/client/extensions/mixins/baseComponents/form/controls/input/translationsInput.js";

export default {
  mixins: [TranslationsInputMixin],
  props: {
    editModalTitle: {
      type: String,
      default: "core.form.translations.editModalTitle",
    },
    translateModalTitle: {
      type: Boolean,
      default: true,
    },
  },
  setup(props) {},
  data() {
    return {
      showModal: false,
      currentEditLanguage: null,
    };
  },
  computed: {
    safeValue() {
      if (!this.value || typeof this.value !== "object") {
        return {};
      }

      return this.value;
    },
    valueLength() {
      return Object.keys(this.safeValue).length;
    },
  },
  watch: {},
  methods: {
    showLanguageForEdit(lang) {
      if (typeof this.value[lang] === "undefined") {
        this.value[lang] = {};
      }
      this.currentEditLanguage = lang;

      this.showModal = true;
    },
    // custom input class implementation
    getInputClass(type) {
      let inputClass = [];

      if (this.inputSize && typeof this.inputSize === "string") {
        inputClass.push("uk-form-width-" + this.inputSize);
      }
      // error class

      if (this.hasError || false) {
        inputClass.push("uk-form-danger");
      }

      // error fixed indicator
      if (this.errorFixed || false) {
        inputClass.push("uk-form-success");
      }

      if (type === "checkbox") {
        inputClass.push("uk-checkbox");
        return inputClass;
      }

      if (type === "radio") {
        inputClass.push("uk-radio");
        return inputClass;
      }

      if (type === "textarea") {
        inputClass.push("uk-textarea");
        return inputClass;
      }

      // select and "input" can have size modifiers
      if (type === "select") {
        inputClass.push("uk-select");
      } else {
        inputClass.push("uk-input");
      }

      if (type === "large") {
        inputClass.push("uk-form-large");
      }

      if (type === "small") {
        inputClass.push("uk-form-small");
      }

      return inputClass;
    },
    startDeleteTranslationFlow(index) {
      this.$s.ui.modal
        .confirm("core.form.translations.deleteTranslationConfirmText")
        .then((confirmed) => {
          delete this.value[index];
        })
        .catch(() => {});
    },
  },
};
</script>

<style scoped lang="scss">
.item-fields-wrapper > *:first-child {
  margin-top: 0 !important;
}
</style>
